<template>
  <form-layout
    class="contract-contact-form"
    :loading="isLoading"
    @save="savePartner"
  >
    <template #title>
      <h1 class="uppercase">{{ titleText }}</h1>
    </template>
    <template #body>
      <bdg-dropdown
        ref="name"
        name="Contact name"
        :options="contactOptions"
        @blur="onContactNameBlur"
        v-model="formData.id"
        searchable
        :required="!typeIsPartnerOrVendor"
      />
      <bdg-dropdown
        ref="type"
        name="Type"
        :options="types"
        v-model="formData.type"
        required
      />
      <bdg-dropdown
        ref="subtype"
        name="Subtype"
        :options="subtypes"
        v-model="formData.subtype"
        v-if="subtypes"
        required
      />
      <bdg-text-field
        ref="companyName"
        name="Company name"
        v-model="formData.company_name"
        :required="typeIsPartnerOrVendor"
      />
      <bdg-text-field name="Title" v-model="formData.title" />
      <bdg-text-field name="Email address" v-model="formData.email" />
      <bdg-phone-number-field
        name="Primary phone"
        v-model="formData.primary_phone"
      />
    </template>
  </form-layout>
</template>

<script>
import FormLayout from "../../layouts/FormLayout";
import BdgTextField from "../../../forms/fields/BdgTextField";
import BdgDropdown from "../../../forms/fields/BdgDropdown";
import BdgPhoneNumberField from "../../../forms/fields/BdgPhoneNumberField";
import {
  ContactTypeLabels,
  ContactTypes,
  InternalSubtypeLabels,
  InternalSubtypes,
  PartnerSubtypeLabels,
  PartnerSubtypes,
  VendorSubtypeLabels,
  VendorSubtypes
} from "../../../constants/contact-enums";
import { validateRequiredRefs } from "../../../helpers";
import { Phone } from "../../../models/Phone";
import { Contract } from "../../../models/Contract";

export default {
  name: "ContractContactForm",
  extends: FormLayout,
  components: { BdgPhoneNumberField, BdgDropdown, BdgTextField, FormLayout },
  props: {
    id: Number,
    name: String,
    type: String,
    subtype: String,
    company_name: String,
    title: String,
    email: String,
    primary_phone: {
      type: Phone,
      default: () => new Phone()
    },
    contract: Contract
  },
  data() {
    return {
      formData: {
        id: this.id,
        contract_id: this.$route.params.id,
        name: this.name,
        type: this.type,
        subtype: this.subtype,
        company_name: this.company_name,
        title: this.title,
        email: this.email,
        primary_phone: this.primary_phone,
        isContractPartner: true
      },
      types: Object.values(ContactTypes).map(slug => {
        return { label: ContactTypeLabels[slug], value: slug };
      })
    };
  },
  computed: {
    titleText() {
      return this.id ? "Edit Contact" : "Add Contact";
    },
    contacts() {
      const contacts = this.$store.state.Contacts.contacts;
      if (contacts && this.contract) {
        return contacts.filter(c => {
          return (
            this.contract.contacts.map(cc => cc.id).indexOf(c.id) === -1 ||
            c.id === this.id
          );
        });
      }
      return contacts;
    },
    contactOptions() {
      if (this.contacts) {
        return this.contacts.map(contact => {
          let label, subLabel;
          if (
            (contact.type === ContactTypes.PARTNER ||
              contact.type === ContactTypes.VENDOR) &&
            contact.company_name
          ) {
            label = contact.company_name;
            subLabel = contact.name;
          } else {
            label = contact.name;
            subLabel = contact.company_name;
          }
          return {
            label,
            subLabel,
            value: contact.id
          };
        });
      }
      return [];
    },
    selectedContact() {
      if (this.formData.id && this.contacts) {
        return this.contacts.find(p => p.id === this.formData.id);
      }
      return null;
    },
    subtypes() {
      if (this.formData.type === ContactTypes.PARTNER) {
        return Object.values(PartnerSubtypes).map(slug => {
          return { label: PartnerSubtypeLabels[slug], value: slug };
        });
      } else if (this.formData.type === ContactTypes.VENDOR) {
        return Object.values(VendorSubtypes).map(slug => {
          return { label: VendorSubtypeLabels[slug], value: slug };
        });
      } else if (this.formData.type === ContactTypes.INTERNAL) {
        return Object.values(InternalSubtypes).map(slug => {
          return { label: InternalSubtypeLabels[slug], value: slug };
        });
      }
      return null;
    },
    typeIsPartnerOrVendor() {
      return (
        this.formData.type === ContactTypes.VENDOR ||
        this.formData.type === ContactTypes.PARTNER
      );
    },
    requiredRefs() {
      const refs = ["name", "type"];

      if (this.typeIsPartnerOrVendor) {
        refs.splice(refs.indexOf("name"), 1);
        refs.push("companyName");
      }

      if (this.subtypes) {
        refs.push("subtype");
      }

      return refs;
    },
    isStoringContact() {
      return this.$store.state.Contacts.isStoringContact;
    },
    isUpdatingContact() {
      return this.$store.state.Contacts.isUpdatingContact;
    },
    isLoading() {
      return this.id ? this.isUpdatingContact : this.isStoringContact;
    }
  },
  watch: {
    selectedContact(newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        this.formData.name = newVal.name;
        this.formData.type = newVal.type;
        this.formData.subtype = newVal.subtype;
        this.formData.company_name = newVal.company_name;
        this.formData.title = newVal.title;
        this.formData.email = newVal.email;
        this.formData.primary_phone = newVal.primary_phone;
      }
    }
  },
  created() {
    this.$store.dispatch("getContacts");
  },
  methods: {
    onContactNameBlur() {
      const contactNameText = this.$refs.name.$refs.dropdown.$refs.search.value;
      if (this.selectedContact) {
        this.formData.name = this.selectedContact.name;
      } else {
        this.formData.name = contactNameText;
      }

      if (
        this.formData.id &&
        this.selectedContact.name !== contactNameText &&
        this.selectedContact.company_name !== contactNameText
      ) {
        this.formData.id = null;
      }
    },
    validate() {
      return validateRequiredRefs(this, true);
    },
    savePartner() {
      if (!this.validate()) {
        return;
      }

      if (this.formData.id) {
        this.$store.dispatch("updateContact", this.formData);
      } else {
        this.$store.dispatch("storeContact", this.formData);
      }
    }
  }
};
</script>

<style scoped></style>
